import React, { useState, useEffect } from 'react';
import { 
  Box, 
  useTheme, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useMediaQuery,
  Chip
} from '@mui/material';
import { tokens } from '../../theme';
import Header from "../../components/Header";
import axiosInstance from '../../api/axiosInstance';
import { endpoints } from '../../api/endPoints';
import { getUserFromCookies } from '../../utils/Cookie-utils';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '@mui/material';

const AllSessionsByTeacherId = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  const user = getUserFromCookies();
  const teacherId = user?.data?.id;
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (teacherId) {
      loadSessions();
    } else {
      setError('Teacher ID not found');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.get(`${endpoints.sessionTimeRanges.getTimetableByTeacher}/${teacherId}/timetable`);
      const sessionsData = response.data.data || [];
      setSessions(sessionsData);
    } catch (error) {
      console.error('Error loading teacher sessions:', error);
      setError(error.response?.data?.message || 'Error loading sessions');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  // Group sessions by day
  const groupedSessions = React.useMemo(() => {
    const grouped = {};
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    
    days.forEach(day => {
      grouped[day] = [];
    });
    
    sessions.forEach(session => {
      if (session.day && grouped[session.day]) {
        grouped[session.day].push(session);
      }
    });
    
    // Sort sessions within each day by sessionTime
    Object.keys(grouped).forEach(day => {
      grouped[day].sort((a, b) => {
        const timeA = a.sessionTime || '';
        const timeB = b.sessionTime || '';
        return timeA.localeCompare(timeB);
      });
    });
    
    return grouped;
  }, [sessions]);

  if (loading) {
    return <LoadingSpinner message="Loading your sessions..." height="400px" />;
  }

  if (error) {
    return (
      <Box sx={{ m: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/classes-for-timetable')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Header title="MY SESSIONS" />
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return (
    <Box sx={{ m: 2 }}>
      <Box display="flex" alignItems="center" mb={2} flexDirection={isSmallScreen ? 'column' : 'row'} gap={2}>
        
        <Header title="MY SESSIONS" />
      </Box>

      {sessions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No sessions assigned
          </Typography>
          <Typography variant="body2" color="textSecondary">
            You don't have any teaching sessions assigned yet.
          </Typography>
        </Box>
      ) : isSmallScreen ? (
        // Box view for small screens
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {days.map(day => {
            const daySessions = groupedSessions[day] || [];
            if (daySessions.length === 0) return null;
            
            return (
              <Box
                key={day}
                sx={{
                  border: `1px solid ${colors.grey[300]}`,
                  borderRadius: 1,
                  boxShadow: 1,
                  backgroundColor: colors.primary[400],
                  p: 2,
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: colors.greenAccent[500] }}>
                  {day}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {daySessions.map((session, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        backgroundColor: colors.primary,
                        border: `1px solid ${colors.grey[300]}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                          {session.sessionTime || 'N/A'}
                        </Typography>
                        {session.combination && (
                          <Chip
                            label={session.combination}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: '24px',
                              backgroundColor: colors.blueAccent[500],
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                        <strong>Subject:</strong> {session.subjectName || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        <strong>Class:</strong> {session.classTitle || 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        // Table view for large screens
        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>Day</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>Session Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: colors.greenAccent[500] }}>Combination</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map(day => {
                const daySessions = groupedSessions[day] || [];
                if (daySessions.length === 0) return null;
                
                return daySessions.map((session, index) => (
                  <TableRow key={`${day}-${index}`}>
                    {index === 0 && (
                      <TableCell
                        rowSpan={daySessions.length}
                        sx={{
                          fontWeight: 'bold',
                          verticalAlign: 'top',
                          borderRight: `1px solid ${colors.grey[300]}`,
                        }}
                      >
                        {day}
                      </TableCell>
                    )}
                    <TableCell>{session.sessionTime || 'N/A'}</TableCell>
                    <TableCell>{session.subjectName || 'N/A'}</TableCell>
                    <TableCell>{session.classTitle || 'N/A'}</TableCell>
                    <TableCell>
                      {session.combination ? (
                        <Chip
                          label={session.combination}
                          size="small"
                          sx={{
                            backgroundColor: colors.blueAccent[500],
                            color: 'white',
                          }}
                        />
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ));
              })}
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No sessions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AllSessionsByTeacherId;

